import React, { useState } from "react";
import { Row, Col, Avatar, Icon } from "antd";
import { connect } from "react-redux";
import * as R from "ramda";
import { compose } from "recompose";
import { withTranslation } from "react-i18next";

import { httpToHttps } from "../../../utils/images";
import { isLoading } from "../../../utils/actions";
import Upload from "../../../components/controls/Upload";
import { getOptimizedImage } from "../../../utils/images";
import {
  updateCompanyProfile,
  UPDATE_COMPANY_PROFILE
} from "../../../store/actions/company";
import {
  updateHeadquarterProfile,
  UPDATE_HEADQUARTER_PROFILE,
  updateAllCompaniesProfiles,
  UPDATE_ALL_COMPANIES_PROFILES
} from "../../../store/actions/headquarter";
import Button from "../../../components/Button";

import SitePreview from "./SitePreview";
import ColorPicker from "./ColorPicker";
import UniversityConfig from "./UniversityConfig";

import styled from "styled-components/macro";

const defaultColors = {
  bodyBackgroundColor: "#f8f8f8",
  headerFontColor: "#fff",
  headerBackgroundColor: "#343a40"
};

const Container = styled.div`
  padding: 2em 3em 1.5em;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 1.5em;
  margin-bottom: 1.5em;
`;

const AttributeSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => (props.isLastSection ? "0" : "16px")};

  h5 {
    margin-bottom: 0;
    font-size: 14px;
    width: 140px;
    flex-shrink: 0;
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;

  .edit-wrapper {
    z-index: 1;
    width: 100%;
    height: 100%;
    position: absolute;
    opacity: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    font-size: 24px;
    border-radius: 4px;
    transition: opacity 0.2s;
  }

  &:hover .edit-wrapper {
    opacity: 1;
  }
`;

const AttributesCol = styled(Col)`
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
`;

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  width: 100%;

  button {
    margin-right: 8px;
  }
`;

const SiteConfig = ({
  t,
  company,
  companyURL,
  isSaving,
  isSavingAll,
  updateCompanyProfile,
  updateHeadquarterProfile,
  updateAllCompaniesProfiles
}) => {
  const [theme, setTheme] = useState(
    R.pipe(
      R.pathOr({}, ["siteConfig", "theme"]),
      R.mergeRight({ ...defaultColors, headerLogo: company.logo })
    )(company)
  );

  const [loginUrl, setLoginUrl] = useState(company.loginUrl);

  const isUniversity = company.privateJobBoard;

  const onSubmit = async () => {
    const updateFn = company.isHeadquarter
      ? updateHeadquarterProfile
      : updateCompanyProfile;

    const formData = { siteConfig: { theme } };

    if (isUniversity) {
      formData.loginUrl = loginUrl;
    }

    await updateFn({
      companyId: company.id,
      formData
    });
  };

  const onSubmitAll = async () => {
    const siteConfig = { theme };
    await updateAllCompaniesProfiles({ company, siteConfig });
  };

  return (
    <Container>
      <Title>{t("company:siteConfig.configuration")}</Title>
      <Row gutter={16} type="flex">
        <AttributesCol span={7}>
          <div>
            <AttributeSection>
              <h5>{t("company:siteConfig.headerBackground")}</h5>
              <ColorPicker
                data-test="header-background-color-picker"
                color={theme.headerBackgroundColor}
                setColor={color =>
                  setTheme({ ...theme, headerBackgroundColor: color })
                }
              />
            </AttributeSection>
            <AttributeSection>
              <h5>{t("company:siteConfig.headerTitle")}</h5>
              <ColorPicker
                data-test="header-font-color-picker"
                color={theme.headerFontColor}
                setColor={color =>
                  setTheme({ ...theme, headerFontColor: color })
                }
              />
            </AttributeSection>
            <AttributeSection>
              <h5>{t("company:siteConfig.background")}</h5>
              <ColorPicker
                data-test="body-background-color-picker"
                color={theme.bodyBackgroundColor}
                setColor={color =>
                  setTheme({ ...theme, bodyBackgroundColor: color })
                }
              />
            </AttributeSection>
            <AttributeSection>
              <h5>{t("company:siteConfig.logo")}</h5>
              <Upload
                fileType="image/*"
                options={{ upload_preset: "opportunity" }}
                action={process.env.REACT_APP_CLOUDINARY_IMAGE_URL}
                onChange={url => setTheme({ ...theme, headerLogo: url })}
              >
                {() => (
                  <AvatarWrapper>
                    <div className="edit-wrapper">
                      <Icon type="edit" style={{ color: "#fff" }} />
                    </div>
                    <Avatar
                      data-test="site-config-logo"
                      size={70}
                      icon="shop"
                      src={getOptimizedImage(theme.headerLogo)}
                      shape="square"
                      style={{ border: "1px solid rgba(0,0,0,0.1)" }}
                    />
                  </AvatarWrapper>
                )}
              </Upload>
            </AttributeSection>
          </div>
        </AttributesCol>
        <Col span={15} offset={1}>
          <SectionTitle>{t("company:siteConfig.preview")}</SectionTitle>
          <SitePreview
            companyURL={companyURL}
            title={company.name}
            {...theme}
          />
        </Col>
      </Row>
      {isUniversity && (
        <>
          <Title style={{ marginTop: "2em" }}>
            {t("vinculations:universities.config")}
          </Title>
          <UniversityConfig
            value={loginUrl}
            onChange={url => setLoginUrl(url)}
          />
        </>
      )}
      <ButtonsContainer>
        <Button
          type="quaternary"
          size="lg"
          key="default-config"
          onClick={() => {
            setTheme({
              ...defaultColors,
              headerLogo: httpToHttps(company.logo)
            });
          }}
          disabled={isSaving || isSavingAll}
        >
          {t("company:siteConfig.defaultConfiguration")}
        </Button>
        <Button
          size="lg"
          key="save"
          type={company.isHeadquarter ? "secondary" : "primary"}
          onClick={onSubmit}
          loading={isSaving}
          disabled={isSavingAll}
          data-test="save-company-config-btn"
        >
          {t("global:save")}
        </Button>
        {company.isHeadquarter && (
          <Button
            size="lg"
            key="save-all"
            type="primary"
            onClick={onSubmitAll}
            loading={isSavingAll}
            disabled={isSaving}
            data-test="save-all-companies-config-btn"
          >
            {t("company:siteConfig.saveAll")}
          </Button>
        )}
      </ButtonsContainer>
    </Container>
  );
};

const enhancer = compose(
  withTranslation(["global", "company"]),
  connect(
    state => ({
      isSaving:
        isLoading(UPDATE_COMPANY_PROFILE, state) ||
        isLoading(UPDATE_HEADQUARTER_PROFILE, state),
      isSavingAll: isLoading(UPDATE_ALL_COMPANIES_PROFILES, state)
    }),
    {
      updateCompanyProfile,
      updateHeadquarterProfile,
      updateAllCompaniesProfiles
    }
  )
);
export default enhancer(SiteConfig);
